import type { Job, RealtimeSnapshot } from '../types'

/**
 * Keeps an Assign / Remove made in this tab on screen until assignment data that includes it
 * arrives. Assignments are cached (and re-read only on change / after a max age), so a refresh
 * could otherwise re-apply an older list and briefly undo the admin's own change.
 */
interface StaffingMutation {
  /** When the server confirmed the change (null while the request is pending). */
  resolvedAt: number | null
  expiresAt: number
}

/** Safety net: never protect a job's local staffing for longer than this. */
const MAX_PROTECT_MS = 2 * 60_000

const mutations = new Map<string, StaffingMutation>()

export function beginStaffingMutation(jobId: string) {
  mutations.set(jobId, { resolvedAt: null, expiresAt: Date.now() + MAX_PROTECT_MS })
}

/** Success: protect until newer data arrives. Failure: the optimistic change was rolled back. */
export function endStaffingMutation(jobId: string, succeeded: boolean) {
  if (!succeeded) {
    mutations.delete(jobId)
    return
  }
  mutations.set(jobId, { resolvedAt: Date.now(), expiresAt: Date.now() + MAX_PROTECT_MS })
}

export function clearStaffingMutations() {
  mutations.clear()
}

/**
 * For jobs changed locally, keeps the current staffing (workers, required count, status) unless
 * the snapshot's assignment data was requested after the change was confirmed.
 */
export function protectOptimisticStaffing(
  snapshot: RealtimeSnapshot,
  currentJobsById: Record<string, Job>,
  now = Date.now(),
): RealtimeSnapshot {
  if (mutations.size === 0) return snapshot
  const asOf = snapshot.staffingAsOf ?? 0
  let changed = false
  const jobs = snapshot.jobs.map((job) => {
    const mutation = mutations.get(job.id)
    if (!mutation) return job
    if (mutation.expiresAt <= now || (mutation.resolvedAt != null && asOf >= mutation.resolvedAt)) {
      mutations.delete(job.id)
      return job
    }
    const current = currentJobsById[job.id]
    if (!current) return job
    changed = true
    const next: Job = { ...job, workers: current.workers, requiredWorkers: current.requiredWorkers, status: current.status }
    // Same key set as the current job, so unchanged jobs compare equal (no highlight flash).
    if (current.staffingPending) next.staffingPending = true
    else delete next.staffingPending
    return next
  })
  return changed ? { ...snapshot, jobs } : snapshot
}
